import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import DropoffLoadSection from '../components/DropoffLoadSection'
import PickupLoadSection from '../components/PickupLoadSection'
import ScaleInfoSection from '../components/ScaleInfoSection'
import SubmitButton from '../components/SubmitButton'
import TagHeader from '../components/TagHeader'
import type { Stop } from '../constants/stopsData'
import { useTagContext } from '../contexts/TagContext'
import { loadService } from '../services/loadService'
import type { LoadFormData, LoadState, LoadStates } from '../types/storageTypes'

const LoadDetails: React.FC = () => {
  const navigate = useNavigate()
  const { getCurrentTag, isLoading, error, tags } = useTagContext()
  const { tagId: urlTagId, loadNumber } = useParams()

  const currentTag = getCurrentTag(urlTagId)
  const tagId = currentTag?.id || urlTagId

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedStops, setSelectedStops] = useState<Stop[]>([])
  const [formData, setFormData] = useState<LoadFormData>({
    scaleTagId: '',
    material: currentTag?.material || '',
    weight: '',
    tolls: '',
    durations: {},
  })

  const [loadStates, setLoadStates] = useState<LoadStates>(() => {
    const getTimeValue = (key: string) => {
      const storageKey = `time_${key}_${tagId}_${loadNumber}`
      // First check localStorage
      const localValue = localStorage.getItem(storageKey)
      console.log(
        `[LoadDetails] Checking localStorage for ${key} with key ${storageKey}:`,
        localValue,
      )
      return localValue
    }

    return {
      loadArriving: {
        timestamp: getTimeValue('loadArriving'),
        isActive: false, // We'll set this based on the timestamp
      },
      loadStart: {
        timestamp: getTimeValue('loadStart'),
        isActive: false,
      },
      loadDeparting: {
        timestamp: getTimeValue('loadDeparting'),
        isActive: false,
      },
      unloadArriving: {
        timestamp: getTimeValue('unloadArriving'),
        isActive: false,
      },
      unloadStart: {
        timestamp: getTimeValue('unloadStart'),
        isActive: false,
      },
      unloadDeparting: {
        timestamp: getTimeValue('unloadDeparting'),
        isActive: false,
      },
    }
  })

  useEffect(() => {
    if (!tagId || !loadNumber) return

    const storedStops = localStorage.getItem(
      `scale_selectedStops_${tagId}_${loadNumber}`,
    )
    if (storedStops) {
      setSelectedStops(JSON.parse(storedStops))
      window.scrollTo(0, 0)
    }
  }, [tagId, loadNumber])

  useEffect(() => {
    if (!tagId || !loadNumber) return

    setFormData({
      scaleTagId:
        localStorage.getItem(`scale_scaleTagId_${tagId}_${loadNumber}`) || '',
      material:
        localStorage.getItem(`scale_material_${tagId}_${loadNumber}`) || '',
      weight: localStorage.getItem(`scale_weight_${tagId}_${loadNumber}`) || '',
      tolls: localStorage.getItem(`scale_tolls_${tagId}_${loadNumber}`) || '',
      durations: selectedStops.reduce(
        (acc, stop) => {
          const duration = localStorage.getItem(
            `scale_duration_${tagId}_${loadNumber}_${stop.id}`,
          )
          if (duration) {
            acc[stop.id] = duration
          }
          return acc
        },
        {} as { [key: string]: string },
      ),
    })
  }, [tagId, loadNumber, selectedStops])

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (error) {
    return <div>Error: {error}</div>
  }

  const handleSubmit = async () => {
    if (!tagId || !loadNumber) return

    setIsSubmitting(true)
    try {
      await loadService.saveLoad(
        String(tagId),
        loadNumber,
        {
          ...formData,
          material: currentTag?.material || '',
        },
        {
          loadStates,
          selectedStops,
        },
      )

      if (currentTag?.status !== 'SIGNED') {
        toast.success('Load details saved successfully')
      }

      if (currentTag?.status === 'SIGNED') {
        navigate(`/tags/open/${tagId}/confirmation`)
      } else {
        navigate(`/tags/open/${tagId}/loads`)
      }
    } catch (error) {
      console.error('Failed to save load details:', error)
      toast.error('Failed to save load details. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid = (): boolean => {
    // Check if at least arrival and departure times are set for both pickup and dropoff
    const requiredTimestamps = [
      loadStates.loadArriving.timestamp,
      loadStates.loadDeparting.timestamp,
      loadStates.unloadArriving.timestamp,
      loadStates.unloadDeparting.timestamp,
    ]

    if (requiredTimestamps.some((timestamp) => !timestamp)) return false

    // Check if all selected stops have durations
    if (selectedStops.length > 0) {
      const allStopsHaveDurations = selectedStops.every(
        (stop) => formData.durations?.[stop.id],
      )
      if (!allStopsHaveDurations) return false
    }

    return true
  }

  return (
    <div className={css(styles.container)}>
      <TagHeader
        tagId={String(tagId)}
        title={`Load #${loadNumber}`}
        status={currentTag?.status || 'IN_PROGRESS'}
      />
      <div className={css(styles.formSection)}>
        <PickupLoadSection
          loadStates={loadStates}
          setLoadStates={setLoadStates}
          tagId={String(tagId)}
          loadNumber={loadNumber}
          isFinal={currentTag?.status === 'COMPLETE'}
          pickupLocation={currentTag?.origin_address || ''}
        />
        <DropoffLoadSection
          loadStates={loadStates}
          setLoadStates={setLoadStates}
          tagId={String(tagId)}
          loadNumber={loadNumber}
          isFinal={currentTag?.status === 'COMPLETE'}
          dropoffLocation={currentTag?.destination_address || ''}
        />
        <ScaleInfoSection
          formData={formData}
          setFormData={setFormData}
          selectedStops={selectedStops}
          setSelectedStops={setSelectedStops}
          tagId={String(tagId)}
          loadNumber={loadNumber}
          isFinal={currentTag?.status === 'COMPLETE'}
        />
        <SubmitButton
          canSubmit={!isSubmitting && isFormValid()}
          onClick={handleSubmit}
          text={
            currentTag?.status === 'COMPLETE'
              ? 'Continue'
              : `Save Load #${loadNumber}`
          }
        />
      </div>
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    alignItems: 'center',
    margin: '0 auto',
    minHeight: '100vh',
  },
  formSection: {
    padding: '4px 0px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    position: 'relative',
    width: '100%',
    flex: 1,
    alignItems: 'center',
  },
})

export default LoadDetails
